import classNames from 'classnames'
import Papa from 'papaparse'
import { useEffect, useMemo, useState } from 'react'

import {
  MANAGEMENT_QUERY_KEY_SEARCH_NON_ALLOCATED_SIMS,
  useManagementCreateNonAllocatedSims,
  useManagementGetNonAllocatedSims
} from '@apps-common/apis/management/non-allocated-sim'
import { useManagementSearchSimApns } from '@apps-common/apis/management/sim-apn/search-sim-apns'
import { ButtonRounded, ButtonRoundSm } from '@apps-common/components/elements'
import { FileSelect } from '@apps-common/components/elements/form/FileSelect'
import { VertSpacerSm } from '@apps-common/components/layout'
import {
  ModalBody,
  ModalCloseButton,
  ModalContainer,
  ModalFooter,
  ModalHeader,
  ModalHeaderContainer
} from '@apps-common/components/modals/modal'
import { commonIcons } from '@apps-common/config/icons'
import { ModalDismiss } from '@apps-common/config/modal'
import { queryClient } from '@apps-common/lib'
import { toastError } from '@apps-common/lib/toast'
import { INonAllocatedSimModel } from '@apps-common/types/management/non-allocated-sim'

const APN_ID_KEY = 'simApnId'

const IMPORT_MAP: any = {
  mobile: 'mobileNo',
  sim: 'simNo',
  apn: APN_ID_KEY,
  ip: 'ip'
}

export const NonAllocatedSimModal = ({ onDismiss }: { onDismiss: (data?: string | null | undefined | number, role?: string) => void }) => {
  const [file, setFile] = useState<File | null>()
  const [importData, setImportData] = useState<INonAllocatedSimModel[] | null>()

  const { isLoading: simsIsLoading, data: sims } = useManagementGetNonAllocatedSims({
    search: ''
  })
  const { isLoading: apnIsLoading, data: apns } = useManagementSearchSimApns({})
  const { isPending: createIsLoading, mutate: createSims } = useManagementCreateNonAllocatedSims(true, () => {
    queryClient.invalidateQueries({ queryKey: MANAGEMENT_QUERY_KEY_SEARCH_NON_ALLOCATED_SIMS() })
    onDismiss(null, ModalDismiss.Confirm)
  })

  const onParseComplete = (result: Papa.ParseResult<any>) => {
    try {
      const parsedData: INonAllocatedSimModel[] = []

      for (let i = 0; i < result.data.length; i++) {
        const item = result.data[i]
        const mappedItem: any = {
          id: 0,
          userId: 0
        }

        Object.keys(item).forEach((key) => {
          const val = item[key]
          const mappedKey = IMPORT_MAP[key.toLowerCase()]

          if (!mappedKey) {
            throw new Error(`Invalid column found in file, invalid column: ${key}.`)
          }

          if (!val || typeof val !== 'string' || val.length === 0) {
            throw new Error(`Invalid column data in file, invalid value found in column ${key}.`)
          }

          if (mappedKey === APN_ID_KEY) {
            const simApn = apns?.find((a) => a.simApnName.toLowerCase() === val.toLowerCase())

            if (!simApn) {
              throw new Error(`Invalid column data in file, invalid APN found in column ${key}.`)
            }

            mappedItem[mappedKey] = simApn.simApnId
          } else {
            mappedItem[mappedKey] = val
          }
        })

        parsedData.push(mappedItem)
      }

      setImportData(parsedData)
    } catch (e: any) {
      setFile(null)
      toastError(e?.message || 'Failed to parse CSV file, invalid file or format.')
    }
  }

  useEffect(() => {
    if (file) {
      console.log('parsing file', file)
      Papa.parse(file, {
        complete: onParseComplete,
        header: true
      })
    }
  }, [file])

  const onSubmit = async () => {
    try {
      createSims({ model: importData || [] })
    } catch (e: any) {
      // ignore, let mutation handle error
    }
  }

  const importHasDuplicate = () => {
    if (importData && sims) {
      const index = importData.findIndex((d: INonAllocatedSimModel) => {
        const exists = sims.findIndex(
          (s) =>
            s.mobileNo.toLowerCase() === d.mobileNo.toLowerCase() ||
            s.simNo.toLowerCase() === d.simNo.toLowerCase() ||
            s.ip.toLowerCase() === d.ip.toLowerCase()
        )

        return exists !== -1
      })

      return index !== -1
    }

    return false
  }

  const hasDuplicate = useMemo(() => importHasDuplicate(), [importData, sims])

  return (
    <ModalContainer innerClasses='!min-h-[640px]'>
      <ModalHeaderContainer>
        <ModalHeader
          subtitle='Import'
          title='Non allocated sims'
          actions={[<ModalCloseButton closeModal={() => onDismiss(null, ModalDismiss.Cancel)} key='close-button' />]}
        >
          {importData && (
            <div className='flex items-end justify-between mr-[10px]'>
              <div className=''>
                <p className='jost text-text text-[0.9em]'>{importData?.length} Sims to import </p>
                {hasDuplicate && (
                  <p className='flex items-center jost gap-[5px] text-[0.8em] bg-error/40 text-on-error border border-error px-2 rounded-full mt-1'>
                    <commonIcons.warning /> Duplicate(s)
                  </p>
                )}
              </div>
              <button
                type='button'
                className='underline jost-medium text-text text-[0.9em] md:text-[1em]'
                disabled={createIsLoading}
                onClick={() => {
                  setFile(null)
                  setImportData(null)
                }}
              >
                Clear
              </button>
            </div>
          )}
        </ModalHeader>
      </ModalHeaderContainer>
      <ModalBody classes='relative !pb-[40px]'>
        <>
          {!importData && (
            <div className='overflow-y-scroll sm:max-h-[80lvh] pt-[95px] pb-[70px] scrollbar-none flex flex-col gap-3 text-[0.9em] md:text-[1em]'>
              <FileSelect
                containerClasses='w-full text-text'
                onChangeCallback={(csvFile: any) => {
                  setFile(csvFile)
                }}
                clearChildren={<div />}
              >
                {(apnIsLoading || simsIsLoading) && <div className='bg-surface-2 w-[220px] h-[20px] rounded-[10px] animate-pulse' />}
                {!apnIsLoading && !simsIsLoading && apns && sims && (
                  <div className='bg-surface border border-border px-6 py-4 rounded-2xl'>
                    <div className='flex items-center justify-start text-text'>
                      <commonIcons.csvFile className=' text-[1.25rem] mr-[10px]' />
                      <p>Select CSV file to import.</p>
                    </div>
                    <VertSpacerSm />
                    <p className='text-text-muted/70 text-left'>File needs to be a .csv and have the following headers: mobile, sim, ip</p>
                  </div>
                )}
              </FileSelect>
            </div>
          )}
          {importData && (
            <div
              className={classNames(
                'overflow-y-scroll sm:max-h-[80lvh] pt-[137px] pb-[60px] scrollbar-none flex flex-col gap-4 text-[0.9em] md:text-[1em]'
              )}
              style={{ scrollbarWidth: 'thin' }}
            >
              {importData.map((d, i) => {
                const key = `non-allocated-import-table-row-${i}`
                const apn = apns?.find((a) => a.simApnId === d.simApnId)
                const existing = sims?.find(
                  (s) =>
                    s.mobileNo.toLowerCase() === d.mobileNo.toLowerCase() ||
                    s.simNo.toLowerCase() === d.simNo.toLowerCase() ||
                    s.ip.toLowerCase() === d.ip.toLowerCase()
                )

                return (
                  <div
                    key={key}
                    className={classNames(
                      'bg-surface text-text text-[0.85em] border border-border jost-medium rounded-2xl p-6 py-4 flex items-center justify-between',
                      existing && 'text-error'
                    )}
                  >
                    <div>
                      <p>{d.mobileNo}</p>
                      <p>{d.simNo}</p>
                      <p>{apn?.simApnName}</p>
                      <p>{d.ip}</p>
                    </div>
                    <div>
                      {existing && (
                        <div className='flex items-center justify-end gap-2 '>
                          <div className='flex items-center justify-center gap-1 border border-border pl-1 pr-2 py-1 rounded-xl'>
                            <commonIcons.warning className='rounded-full text-error text-[1.5rem]' />
                            <span className='text-[0.7rem] leading-[0.9rem] jost-medium text-center'>
                              duplicate
                              <br />
                              detected
                            </span>
                          </div>
                          <ButtonRoundSm type='button' className='bg-error text-on-error'>
                            <commonIcons.remove />
                          </ButtonRoundSm>
                        </div>
                      )}
                      {!existing && (
                        <commonIcons.checkCircle className='mr-[5px] bg-success text-on-success p-1 rounded-full text-[1.8rem]' />
                      )}
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </>
      </ModalBody>
      <ModalFooter>
        <div className='flex items-center justify-start gap-2'>
          <ButtonRounded
            type='button'
            loading={createIsLoading || !importData || hasDuplicate}
            text='Import'
            className='action-success'
            callback={() => onSubmit()}
          />
        </div>
      </ModalFooter>
    </ModalContainer>
  )
}
